import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, TrendingUp, Share2, Trash2, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAPY, setNewAPY] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, entriesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/waitlist`)
      ]);
      setStats(statsRes.data);
      setEntries(entriesRes.data.entries);
      setNewAPY(statsRes.data.current_apy.toString());
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateAPY = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      await axios.put(`${API}/settings/apy`, {
        apy_rate: parseFloat(newAPY)
      });
      alert('APY updated successfully!');
      fetchData();
    } catch (error) {
      console.error('Error updating APY:', error);
      alert('Failed to update APY');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Delete ${email} from waitlist?`)) return;
    
    try {
      await axios.delete(`${API}/admin/waitlist/${encodeURIComponent(email)}`);
      alert('User deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading admin data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="bg-white p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all shadow-sm"
            >
              <ArrowLeft className="w-5 h-5 text-slate-700" />
            </button>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <h1 className="text-4xl font-bold text-slate-900">Saverr Admin</h1>
              </div>
              <p className="text-slate-600">Manage your waitlist and platform settings</p>
            </div>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 font-semibold"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-100 p-2.5 rounded-xl">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-slate-600 font-semibold text-sm">Total Users</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-emerald-100 p-2.5 rounded-xl">
                <Share2 className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-slate-600 font-semibold text-sm">Total Referrals</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{stats?.total_referrals || 0}</p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-purple-100 p-2.5 rounded-xl">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-slate-600 font-semibold text-sm">Avg Referrals</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{stats?.average_referrals || 0}</p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-2xl border-2 border-blue-200 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-slate-600 font-semibold text-sm">Current APY</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{stats?.current_apy}%</p>
          </div>
        </div>

        {/* APY Update Form */}
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Update APY Rate</h2>
          <form onSubmit={handleUpdateAPY} className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                New APY Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={newAPY}
                onChange={(e) => setNewAPY(e.target.value)}
                className="w-full px-5 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:outline-none text-lg font-semibold text-slate-900"
                required
              />
            </div>
            <button
              type="submit"
              disabled={updating}
              className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? 'Updating...' : 'Update APY'}
            </button>
          </form>
        </div>

        {/* Top Referrers */}
        {stats?.top_referrers && stats.top_referrers.length > 0 && (
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Top Referrers</h2>
            <div className="space-y-3">
              {stats.top_referrers.slice(0, 5).map((user, index) => (
                <div key={user.email} className="flex items-center justify-between p-5 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-600 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg shadow-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">{user.email}</p>
                      <p className="text-sm text-slate-500">Position #{user.position}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-blue-600">{user.referral_count}</p>
                    <p className="text-sm text-slate-500 font-medium">referrals</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Waitlist Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200 bg-slate-50">
            <h2 className="text-2xl font-bold text-slate-900">Waitlist Entries ({entries.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-slate-700">Position</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-slate-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-slate-700">Referrals</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-slate-700">Referral Code</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-slate-700">Referred By</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-slate-700">Joined</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-bold text-blue-600">#{entry.position}</td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{entry.email}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-semibold">
                        {entry.referral_count}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-slate-600 bg-slate-50">{entry.referral_code}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{entry.referred_by || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleDelete(entry.email)}
                        className="text-red-600 hover:text-red-800 transition-colors p-2 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
